import type { PhoneNumber } from '@1trading/fast-api-domain/forms';
import { z } from 'zod';

import {
  INSTITUTION_ADDRESS_DEFAULTS,
  type InstitutionAddress,
  institutionAddressSchema,
  institutionAddressToInstitutionDetailsAddressApi,
  institutionDetailsAddressApiToInstitutionAddress,
} from '@/features/institution/schemas/address';
import {
  createTaxResidencesSchema,
  TAX_RESIDENCE_DEFAULTS,
  type TaxResidence,
  taxResidenceApiToTaxResidence,
  taxResidenceToTaxResidenceApi,
} from '@/features/tax-residency/schema';
import i18n from '@/lib/i18n';
import { type InstitutionDetailsV2ContactablePerson, institutionDetailsV2ContactablePersonSchema } from '@/services/api/profile';
import { type DateParts, EMPTY_DATE_PARTS, isDatePartsValid, toDatePartsFromString, toDateStringFromParts } from '@/utils/date';
import { EMPTY_PHONE_NUMBER } from '@/utils/phone-number';

export type InstitutionPerson = {
  partyId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: DateParts;
  countryOfBirth: string;
  localityOfBirth: string;
  address: InstitutionAddress;
  nationality: string;
  email: string;
  phoneNumber: PhoneNumber;
  taxResidences: TaxResidence[];
};

export const institutionPersonSchema = z.object({
  partyId: institutionDetailsV2ContactablePersonSchema.shape.partyId,
  firstName: institutionDetailsV2ContactablePersonSchema.shape.firstName,
  lastName: institutionDetailsV2ContactablePersonSchema.shape.lastName,
  dateOfBirth: z
    .object({
      day: z.string().regex(/^\d{1,2}$/, i18n.t('institutionDetails.person.errors.dateOfBirth')),
      month: z.string().regex(/^\d{1,2}$/, i18n.t('institutionDetails.person.errors.dateOfBirth')),
      year: z.string().regex(/^\d{4}$/, i18n.t('institutionDetails.person.errors.dateOfBirth')),
    })
    .refine(isDatePartsValid, { error: i18n.t('institutionDetails.person.errors.dateOfBirth'), when: ({ issues }) => issues.length === 0 }),
  countryOfBirth: institutionDetailsV2ContactablePersonSchema.shape.countryOfBirth,
  localityOfBirth: institutionDetailsV2ContactablePersonSchema.shape.localityOfBirth,
  address: institutionAddressSchema,
  nationality: institutionDetailsV2ContactablePersonSchema.shape.nationality,
  email: institutionDetailsV2ContactablePersonSchema.shape.email,
  phoneNumber: institutionDetailsV2ContactablePersonSchema.shape.phoneNumber,
  taxResidences: createTaxResidencesSchema('individual'),
});

export const createInstitutionPerson = (): InstitutionPerson => ({
  partyId: crypto.randomUUID(),
  firstName: '',
  lastName: '',
  dateOfBirth: EMPTY_DATE_PARTS,
  countryOfBirth: '',
  localityOfBirth: '',
  address: INSTITUTION_ADDRESS_DEFAULTS,
  nationality: '',
  email: '',
  phoneNumber: EMPTY_PHONE_NUMBER,
  taxResidences: [TAX_RESIDENCE_DEFAULTS],
});

export const institutionPersonToInstitutionDetailsPersonApi = ({
  partyId,
  firstName,
  lastName,
  dateOfBirth,
  countryOfBirth,
  localityOfBirth,
  address,
  nationality,
  email,
  phoneNumber,
  taxResidences,
}: InstitutionPerson): InstitutionDetailsV2ContactablePerson => ({
  partyId,
  firstName,
  lastName,
  dateOfBirth: toDateStringFromParts(dateOfBirth),
  countryOfBirth,
  localityOfBirth,
  address: institutionAddressToInstitutionDetailsAddressApi(address),
  nationality,
  email,
  phoneNumber,
  taxResidences: taxResidences.map(taxResidenceToTaxResidenceApi),
});

export const institutionDetailsPersonApiToInstitutionPerson = ({
  partyId,
  firstName,
  lastName,
  dateOfBirth,
  countryOfBirth,
  localityOfBirth,
  address,
  nationality,
  email,
  phoneNumber,
  taxResidences,
}: InstitutionDetailsV2ContactablePerson): InstitutionPerson => ({
  partyId,
  firstName,
  lastName,
  dateOfBirth: toDatePartsFromString(dateOfBirth),
  countryOfBirth,
  localityOfBirth,
  address: institutionDetailsAddressApiToInstitutionAddress(address),
  nationality,
  email,
  // A person saved with an E.164 `phone` has no phoneNumber, which no field would report as missing
  phoneNumber: phoneNumber ?? EMPTY_PHONE_NUMBER,
  taxResidences: taxResidences.map(taxResidenceApiToTaxResidence),
});
